import { assertDocumentTemplateFamilyAdmission } from "@cogo/schema/document-template-family-admission";
import type {
  CogoDocumentTemplateFamily,
  CogoDocumentTemplateFamilyLegacyRef,
  CogoDocumentTemplateFamilyTarget,
  CogoDocumentTemplateFamilyVariant,
} from "@cogo/schema/document-template-family-runtime";
import {
  canonicalizeDocumentTemplateJson,
  compileDocumentTemplate,
  sha256CanonicalJson,
  type CogoDocumentGenerationIr,
  type CogoDocumentRenderTarget,
  type CogoDocumentTemplateManifest,
} from "@cogo/schema/document-template-runtime";
import { readCogoDocumentTemplateRuntimeBundle } from "../model/document-template-generation";

export const COGO_DOCUMENT_TEMPLATE_FAMILY_COMPILE_RECEIPT_SCHEMA_VERSION =
  "cogo.document-template-family-compile-receipt.v1" as const;

export interface CogoDocumentTemplateFamilyCompileReceipt {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_FAMILY_COMPILE_RECEIPT_SCHEMA_VERSION;
  compiledAt: string;
  familyId: string;
  familyVersion: string;
  familyRevision: number;
  familyHash: string;
  variantId: string;
  generationIrHash: string;
  sourceRef: string;
  sourceHash: string;
  requiredTargets: CogoDocumentTemplateFamilyTarget[];
  nativeArtifactStatus: "pending-native-render";
  modelFinalWriterAllowed: false;
  receiptHash: string;
}

export interface CogoDocumentTemplateFamilyCompileResult {
  generationIr: CogoDocumentGenerationIr;
  receipt: CogoDocumentTemplateFamilyCompileReceipt;
}

export interface CompileDocumentTemplateFamilyCandidateInput {
  family: unknown;
  familyRevision: number;
  familyHash: string;
  selectedVariantId: string;
  semanticInput: unknown;
  sourceRef: string;
  sourceHash: string;
  compiledAt: string;
}

function assertFamilyRevision(familyRevision: number): void {
  if (!Number.isInteger(familyRevision) || familyRevision < 1) {
    throw new Error("familyRevision must be a positive integer.");
  }
}

export async function hashCogoDocumentTemplateFamilyCompileReceipt(
  receipt: Omit<CogoDocumentTemplateFamilyCompileReceipt, "receiptHash">,
): Promise<string> {
  return sha256CanonicalJson(receipt);
}

function exactJsonMatch(left: unknown, right: unknown): boolean {
  return (
    canonicalizeDocumentTemplateJson(left) ===
    canonicalizeDocumentTemplateJson(right)
  );
}

function currentLegacyRef(input: {
  family: CogoDocumentTemplateFamily;
  manifest: CogoDocumentTemplateManifest;
  runtimeFamily: CogoDocumentTemplateFamily;
}): CogoDocumentTemplateFamilyLegacyRef {
  const runtimeRef = input.runtimeFamily.legacyTemplateRefs.find(
    (candidate) =>
      candidate.templateId === input.manifest.templateId &&
      candidate.templateVersion === input.manifest.templateVersion,
  );
  const candidateRefs = input.family.legacyTemplateRefs.filter(
    (candidate) =>
      candidate.templateId === input.manifest.templateId &&
      candidate.templateVersion === input.manifest.templateVersion,
  );
  const candidateRef = candidateRefs[0];
  if (
    !runtimeRef ||
    candidateRefs.length !== 1 ||
    !candidateRef ||
    !exactJsonMatch(candidateRef, runtimeRef)
  ) {
    throw new Error(
      "Template family legacy ref does not exactly match the current v1 runtime bundle.",
    );
  }
  return candidateRef;
}

function soleLegacyTemplateId(family: CogoDocumentTemplateFamily): string {
  const legacyRef = family.legacyTemplateRefs[0];
  if (family.legacyTemplateRefs.length !== 1 || !legacyRef) {
    throw new Error(
      "Template family candidate compilation requires exactly one current v1 legacy ref.",
    );
  }
  return legacyRef.templateId;
}

function selectedFlowVariant(input: {
  family: CogoDocumentTemplateFamily;
  selectedVariantId: string;
}): Extract<CogoDocumentTemplateFamilyVariant, { layoutProfile: "flow-page" }> {
  const variant = input.family.variants.find(
    (candidate) => candidate.variantId === input.selectedVariantId,
  );
  if (!variant) {
    throw new Error(
      `Document template family variant "${input.selectedVariantId}" was not found.`,
    );
  }
  if (variant.layoutProfile !== "flow-page") {
    throw new Error(
      `Document template family variant "${input.selectedVariantId}" is not a flow-page variant.`,
    );
  }
  return variant;
}

function assertLegacySlotCompatibility(input: {
  family: CogoDocumentTemplateFamily;
  variant: Extract<
    CogoDocumentTemplateFamilyVariant,
    { layoutProfile: "flow-page" }
  >;
  runtimeVariant: Extract<
    CogoDocumentTemplateFamilyVariant,
    { layoutProfile: "flow-page" }
  >;
  legacyRef: CogoDocumentTemplateFamilyLegacyRef;
  manifest: CogoDocumentTemplateManifest;
}): void {
  const manifestSlots = input.manifest.slots;
  const familySlots = input.family.semanticCore.slots;
  if (
    familySlots.length !== manifestSlots.length ||
    input.variant.approvedOmissions.length !== 0
  ) {
    throw new Error(
      "Template family slots and flow-page bindings are not one-to-one compatible with the current v1 manifest.",
    );
  }

  for (const [index, manifestSlot] of manifestSlots.entries()) {
    const expectedRef = `${input.legacyRef.manifestRef}#/slots/${index}`;
    const familySlot = familySlots.find(
      (candidate) => candidate.id === manifestSlot.id,
    );
    if (
      !familySlot ||
      !("slotRef" in familySlot) ||
      familySlot.slotRef !== expectedRef ||
      familySlot.kind !== manifestSlot.kind ||
      familySlot.required !== manifestSlot.required
    ) {
      throw new Error(
        `Template family slot "${manifestSlot.id}" is not one-to-one compatible with the current v1 manifest.`,
      );
    }
  }

  const legacyLayout = {
    ...input.runtimeVariant.layout,
    layoutRef: `${input.legacyRef.manifestRef}#/layout`,
  };
  const legacyBindings = input.runtimeVariant.slotBindings.map((binding) => {
    const manifestIndex = manifestSlots.findIndex(
      (slot) => slot.id === binding.slotId,
    );
    if (manifestIndex === -1) {
      throw new Error(
        `Trusted runtime flow-page binding "${binding.slotId}" does not match the current v1 manifest.`,
      );
    }
    return {
      ...binding,
      bindingRef: `${input.legacyRef.manifestRef}#/slots/${manifestIndex}`,
    };
  });
  const exactRuntimeVariant =
    exactJsonMatch(input.variant.layout, input.runtimeVariant.layout) &&
    exactJsonMatch(
      input.variant.slotBindings,
      input.runtimeVariant.slotBindings,
    );
  const exactLegacyProjection =
    exactJsonMatch(input.variant.layout, legacyLayout) &&
    exactJsonMatch(input.variant.slotBindings, legacyBindings);
  if (!exactRuntimeVariant && !exactLegacyProjection) {
    throw new Error(
      "Flow-page layout and slot bindings must exactly match either the trusted runtime family variant or its complete legacy manifest projection.",
    );
  }
}

function requiredLegacyTargets(input: {
  variant: Extract<
    CogoDocumentTemplateFamilyVariant,
    { layoutProfile: "flow-page" }
  >;
  manifest: CogoDocumentTemplateManifest;
}): CogoDocumentRenderTarget[] {
  const requiredTargets = [...input.manifest.renderTargets];
  const variantTargets = new Set<CogoDocumentRenderTarget>(
    input.variant.renderTargets,
  );
  if (
    requiredTargets.length === 0 ||
    requiredTargets.some((target) => !variantTargets.has(target))
  ) {
    throw new Error(
      "Flow-page variant does not declare every render target required by the current v1 manifest.",
    );
  }
  return requiredTargets;
}

export async function compileDocumentTemplateFamilyCandidate(
  input: CompileDocumentTemplateFamilyCandidateInput,
): Promise<CogoDocumentTemplateFamilyCompileResult> {
  assertFamilyRevision(input.familyRevision);
  const familyCandidate = structuredClone(input.family);
  assertDocumentTemplateFamilyAdmission(familyCandidate);
  const family = familyCandidate as CogoDocumentTemplateFamily;
  const observedFamilyHash = await sha256CanonicalJson(family);
  if (observedFamilyHash !== input.familyHash) {
    throw new Error(
      "Document template family hash is stale or does not match the admitted family.",
    );
  }

  const legacyTemplateId = soleLegacyTemplateId(family);
  const runtime = readCogoDocumentTemplateRuntimeBundle(legacyTemplateId);
  if (!runtime.manifest) {
    throw new Error(
      `Document template ${legacyTemplateId} has no legacy manifest to compile against.`,
    );
  }
  const manifest = runtime.manifest;
  const legacyRef = currentLegacyRef({
    family,
    manifest,
    runtimeFamily: runtime.family,
  });
  const variant = selectedFlowVariant({
    family,
    selectedVariantId: input.selectedVariantId,
  });
  const runtimeVariant = selectedFlowVariant({
    family: runtime.family,
    selectedVariantId: input.selectedVariantId,
  });
  assertLegacySlotCompatibility({
    family,
    variant,
    runtimeVariant,
    legacyRef,
    manifest,
  });
  const requiredRenderTargets = requiredLegacyTargets({
    variant,
    manifest,
  });
  const requiredTargets: CogoDocumentTemplateFamilyTarget[] = [
    ...requiredRenderTargets,
    ...variant.publicationTargets,
  ];

  const generationIr = await compileDocumentTemplate({
    template: manifest,
    inputSchema: runtime.inputSchema,
    semanticInput: input.semanticInput,
    sourceRef: input.sourceRef,
    sourceHash: input.sourceHash,
    semanticInputRef: `family-candidate://${family.familyId}/revisions/${input.familyRevision}/variants/${variant.variantId}/semantic-input`,
    requestedTargets: requiredRenderTargets,
    createdAt: input.compiledAt,
  });

  const generationIrHash = await sha256CanonicalJson(generationIr);
  const receiptBasis: Omit<
    CogoDocumentTemplateFamilyCompileReceipt,
    "receiptHash"
  > = {
    schemaVersion: COGO_DOCUMENT_TEMPLATE_FAMILY_COMPILE_RECEIPT_SCHEMA_VERSION,
    compiledAt: input.compiledAt,
    familyId: family.familyId,
    familyVersion: family.familyVersion,
    familyRevision: input.familyRevision,
    familyHash: observedFamilyHash,
    variantId: variant.variantId,
    generationIrHash,
    sourceRef: input.sourceRef,
    sourceHash: input.sourceHash,
    requiredTargets,
    nativeArtifactStatus: "pending-native-render",
    modelFinalWriterAllowed: false,
  };
  return {
    generationIr,
    receipt: {
      ...receiptBasis,
      receiptHash:
        await hashCogoDocumentTemplateFamilyCompileReceipt(receiptBasis),
    },
  };
}
